/**
 * Production Batch Detail Modal Component
 * Displays detailed production batch information with tabs
 *
 * @author SAUBHAGYA Development Team
 * @since Story 5-5
 *
 * @description
 * Modal component showing:
 * - Header with batch code and status badge
 * - Tabs: Overview | Inputs | Timeline
 * - Overview: quantities, dates, notes, efficiency
 * - Inputs: table of consumed materials
 * - Timeline: status change history
 * - Action buttons based on status (Start/Complete/Cancel)
 *
 * @example
 * ```tsx
 * const [batchId, setBatchId] = useState<number | null>(null);
 *
 * <ProductionBatchDetailModal
 *   open={batchId !== null}
 *   onOpenChange={(open) => !open && setBatchId(null)}
 *   batchId={batchId}
 *   onStart={(batch) => console.log('Batch started:', batch)}
 *   onComplete={(batch) => console.log('Batch completed:', batch)}
 * />
 * ```
 */

import React, { useState, useRef } from 'react';
import { format } from 'date-fns';
import {
  AlertCircle,
  Package,
  Calendar,
  Clock,
  Factory,
  PlayCircle,
  CheckCircle2,
  XCircle,
  TrendingUp,
  ShoppingBag,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';

import { useProductionBatch, useStartProductionBatch, useCancelProductionBatch, useMarkProductionBatchReadyForSale } from '@/hooks/useProductionBatches';
import { BatchStatusBadge } from '../badges/BatchStatusBadge';
import { ProductionBatchDTO } from '@/services/manufacturing/manufacturingApi';
import { toast } from 'sonner';

interface ProductionBatchDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchId: number | null;
  onStart?: (batch: ProductionBatchDTO) => void;
  onComplete?: (batch: ProductionBatchDTO) => void;
  onCancel?: (batch: ProductionBatchDTO) => void;
}

/**
 * Format date time
 */
function formatDateTime(dateString: string): string {
  return format(new Date(dateString), 'PPp');
}

/**
 * Format date only
 */
function formatDate(dateString: string): string {
  return format(new Date(dateString), 'PP');
}

/**
 * Loading skeleton for modal content
 */
function BatchDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
      <Separator />
      <div className="space-y-3">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-20" />
      </div>
    </div>
  );
}

/**
 * Production Batch Detail Modal Component
 */
export function ProductionBatchDetailModal({
  open,
  onOpenChange,
  batchId,
  onStart,
  onComplete,
  onCancel,
}: ProductionBatchDetailModalProps) {
  const { data: batch, isLoading, error } = useProductionBatch(batchId, open);
  const [activeTab, setActiveTab] = useState('overview');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [readyForSaleDialogOpen, setReadyForSaleDialogOpen] = useState(false);
  const [readyForSaleNotes, setReadyForSaleNotes] = useState('');
  const timelineScrollRef = useRef<HTMLDivElement>(null);

  // Mutations
  const startBatch = useStartProductionBatch();
  const cancelBatch = useCancelProductionBatch();
  const markReadyForSale = useMarkProductionBatchReadyForSale();

  // Calculate efficiency if completed
  const efficiency =
    batch && batch.outputQuantityKg
      ? (batch.outputQuantityKg / batch.totalInputQuantityKg) * 100
      : null;

  // Handle start batch
  const handleStart = async () => {
    if (!batch) return;

    if (!confirm(`Are you sure you want to start batch ${batch.batchCode}?`)) {
      return;
    }

    try {
      const result = await startBatch.mutateAsync(batch.id);
      toast.success(`Batch ${batch.batchCode} started successfully`);
      onStart?.(result);
    } catch (error: any) {
      toast.error(error.message || 'Failed to start batch');
    }
  };

  // Handle open cancel dialog
  const handleOpenCancelDialog = () => {
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  // Handle cancel batch
  const handleConfirmCancel = async () => {
    if (!batch) return;

    try {
      const result = await cancelBatch.mutateAsync({
        batchId: batch.id,
        reason: cancelReason || undefined,
      });
      toast.success(`Batch ${batch.batchCode} cancelled successfully`);
      setCancelDialogOpen(false);
      setCancelReason('');
      onCancel?.(result);
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel batch');
    }
  };

  // Handle open ready for sale dialog
  const handleOpenReadyForSaleDialog = () => {
    setReadyForSaleNotes('');
    setReadyForSaleDialogOpen(true);
  };

  // Handle mark ready for sale
  const handleConfirmReadyForSale = async () => {
    if (!batch) return;

    try {
      const result = await markReadyForSale.mutateAsync({
        batchId: batch.id,
        notes: readyForSaleNotes || undefined,
      });
      toast.success(`Batch ${batch.batchCode} marked as ready for sale`);
      setReadyForSaleDialogOpen(false);
      setReadyForSaleNotes('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to mark batch ready for sale');
    }
  };

  // Handle timeline scroll
  const scrollTimeline = (direction: 'up' | 'down') => {
    if (timelineScrollRef.current) {
      const scrollAmount = 300;
      const newScrollTop =
        direction === 'up'
          ? timelineScrollRef.current.scrollTop - scrollAmount
          : timelineScrollRef.current.scrollTop + scrollAmount;
      timelineScrollRef.current.scrollTo({
        top: newScrollTop,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Production Batch Details</span>
            {batch && <BatchStatusBadge status={batch.status} size="lg" />}
          </DialogTitle>
          <DialogDescription>
            {batch
              ? `Batch Code: ${batch.batchCode} - ${batch.dungProductName}`
              : 'Loading batch details...'}
          </DialogDescription>
        </DialogHeader>

        {isLoading && <BatchDetailSkeleton />}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load batch details. Please try again.
            </AlertDescription>
          </Alert>
        )}

        {batch && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="inputs">Inputs ({batch.inputs?.length || 0})</TabsTrigger>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4">
              {/* Batch Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Factory className="h-4 w-4" />
                      Manufacturing Unit
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium">{batch.manufacturingUnitName}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Dung Product
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium">{batch.dungProductName}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Quantities */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Input Quantity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      {batch.totalInputQuantityKg.toFixed(2)} kg
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Output Quantity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      {batch.outputQuantityKg
                        ? `${batch.outputQuantityKg.toFixed(2)} kg`
                        : '-'}
                    </p>
                  </CardContent>
                </Card>

                {efficiency !== null && (
                  <Card className="border-blue-200 dark:border-blue-800">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        Efficiency
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {efficiency.toFixed(2)}%
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Created
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{formatDate(batch.createdAt)}</p>
                  </CardContent>
                </Card>

                {batch.startedAt && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Started
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm">{formatDate(batch.startedAt)}</p>
                    </CardContent>
                  </Card>
                )}

                {batch.completedAt && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" />
                        Completed
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm">{formatDate(batch.completedAt)}</p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Notes */}
              {batch.notes && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {batch.notes}
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Inputs Tab */}
            <TabsContent value="inputs" className="space-y-4">
              {batch.inputs && batch.inputs.length > 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Batch Code</TableHead>
                          <TableHead>Material Type</TableHead>
                          <TableHead>Supplier</TableHead>
                          <TableHead className="text-right">Quantity (kg)</TableHead>
                          <TableHead>Consumed At</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {batch.inputs.map((input) => (
                          <TableRow key={input.id}>
                            <TableCell className="font-mono text-xs">
                              {input.transactionBatchCode}
                            </TableCell>
                            <TableCell>{input.rawMaterialTypeName}</TableCell>
                            <TableCell>{input.supplierName}</TableCell>
                            <TableCell className="text-right font-medium">
                              {input.consumedQuantityKg.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDateTime(input.consumedAt)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="font-semibold bg-muted/50">
                          <TableCell colSpan={3}>Total</TableCell>
                          <TableCell className="text-right">
                            {batch.totalInputQuantityKg.toFixed(2)} kg
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ) : (
                <Alert>
                  <AlertDescription>No input materials recorded yet.</AlertDescription>
                </Alert>
              )}
            </TabsContent>

            {/* Timeline Tab */}
            <TabsContent value="timeline" className="space-y-3">
              {batch.timeline && batch.timeline.length > 0 ? (
                <div className="space-y-2">
                  {/* Scroll Up Button */}
                  <div className="flex justify-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => scrollTimeline('up')}
                      className="h-8 w-8 p-0"
                      title="Scroll up"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Scrollable Timeline Container */}
                  <div
                    ref={timelineScrollRef}
                    className="max-h-96 overflow-y-auto pr-2 space-y-2"
                  >
                    {batch.timeline.map((event) => (
                      <Card key={event.id} className="border-l-4 border-l-blue-500">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <Badge className="mb-2">{event.statusDisplayName}</Badge>
                              {event.notes && (
                                <p className="text-sm text-muted-foreground mt-2">
                                  {event.notes}
                                </p>
                              )}
                            </div>
                            <time className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDateTime(event.changedAt)}
                            </time>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Scroll Down Button */}
                  <div className="flex justify-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => scrollTimeline('down')}
                      className="h-8 w-8 p-0"
                      title="Scroll down"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <Alert>
                  <AlertDescription>No timeline events available.</AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Action Buttons */}
        {batch && (
          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            {batch.status === 'CREATED' && (
              <>
                <Button
                  variant="outline"
                  onClick={handleOpenCancelDialog}
                  disabled={cancelBatch.isPending}
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancel Batch
                </Button>
                <Button onClick={handleStart} disabled={startBatch.isPending}>
                  <PlayCircle className="mr-2 h-4 w-4" />
                  Start Production
                </Button>
              </>
            )}
            {batch.status === 'PROCESSING' && (
              <>
                <Button
                  variant="outline"
                  onClick={handleOpenCancelDialog}
                  disabled={cancelBatch.isPending}
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancel Batch
                </Button>
                <Button onClick={() => onComplete?.(batch)}>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Complete Batch
                </Button>
              </>
            )}
            {batch.status === 'MANUFACTURED' && (
              <>
                <Button
                  onClick={handleOpenReadyForSaleDialog}
                  disabled={markReadyForSale.isPending}
                >
                  <ShoppingBag className="mr-2 h-4 w-4" />
                  Mark Ready for Sale
                </Button>
              </>
            )}
            {(batch.status === 'READY_FOR_SALE' || batch.status === 'CANCELLED') && (
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Cancel Batch Dialog */}
    <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel Batch {batch?.batchCode}</DialogTitle>
          <DialogDescription>
            Please provide a reason for cancelling this batch (optional)
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            placeholder="Enter cancellation reason..."
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={cancelBatch.isPending}
            >
              {cancelBatch.isPending ? 'Cancelling...' : 'Confirm Cancel'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Mark Ready for Sale Dialog */}
    <Dialog open={readyForSaleDialogOpen} onOpenChange={setReadyForSaleDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark Ready for Sale</DialogTitle>
          <DialogDescription>
            Mark batch {batch?.batchCode} as ready for sale (optional notes)
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            placeholder="Enter any notes (optional)..."
            value={readyForSaleNotes}
            onChange={(e) => setReadyForSaleNotes(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setReadyForSaleDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmReadyForSale}
              disabled={markReadyForSale.isPending}
            >
              {markReadyForSale.isPending ? 'Marking...' : 'Mark Ready for Sale'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default ProductionBatchDetailModal;
